import { collection, collectionGroup, deleteDoc, doc, getDoc, getDocs, onSnapshot, query, updateDoc, where } from 'firebase/firestore';
import { CreateWorkspaceRequest, CreateWorkspaceResponse, ChangeMemberRoleRequest, ChangeMemberRoleResponse, DeleteRoomAsAdminRequest, DeleteRoomAsAdminResponse, GetWorkspaceRoomsRequest, GetWorkspaceRoomsResponse, GetWSByInvitationKeyRequest, GetWSByInvitationKeyResponse, JoinRoomAsCreatorRequest, JoinRoomAsCreatorResponse, JoinRoomViaWorkspaceRequest, JoinRoomViaWorkspaceResponse, JoinWorkspaceRequest, JoinWorkspaceResponse, RemoveWorkspaceMemberRequest, RemoveWorkspaceMemberResponse, TransferRoomCreatorRequest, TransferRoomCreatorResponse } from '../models/DTO';
import { Room, RoomSimplified } from '../models/Room';
import { Workspace, WorkspaceMember, toUserSimplified } from '../models/Workspace';
import { WorkspaceStatus } from '../models/WorkspaceStatus';
import { UnifiedStorageReport } from '../models/UnifiedStorageReport';
import { FireClient, getFunc } from './FireClient';
import { pojo } from './Utils/Util';
import { My } from './My';

export class FireClientWorkspace {
  constructor(private c: FireClient) {
  }

  async createWorkspace(data: CreateWorkspaceRequest): Promise<CreateWorkspaceResponse> {
    const f = getFunc('createWorkspace');
    const res = await f(pojo(data));
    return res.data as CreateWorkspaceResponse;
  }

  async joinWorkspace(data: JoinWorkspaceRequest): Promise<JoinWorkspaceResponse> {
    const f = getFunc('joinWorkspace');
    const res = await f(pojo(data));
    return res.data as JoinWorkspaceResponse;
  }

  async getWorkspaceRooms(data: GetWorkspaceRoomsRequest): Promise<GetWorkspaceRoomsResponse> {
    const wsId = data.workspaceId;
    const userId = My.user?.id;
    if (!userId) return { rooms: [], status: 'ERROR' };

    // 1. 멤버 확인
    const memberDoc = await getDoc(doc(this.c.store, Workspace.COL, wsId, WorkspaceMember.COL, userId));
    if (!memberDoc.exists()) {
      return { rooms: [], status: 'NOT_MEMBER' };
    }

    // 2. 워크스페이스 조회 (오너 여부 확인)
    const wsDoc = await getDoc(doc(this.c.store, Workspace.COL, wsId));
    const ws = wsDoc.data() as Workspace | undefined;
    if (!ws) return { rooms: [], status: 'ERROR' };

    // 3. 방 목록 쿼리 (오너면 전체, 아니면 listed만)
    let q;
    if (ws.owner.id === userId) {
      q = query(collection(this.c.store, 'rooms'), where('workspaceId', '==', wsId));
    } else {
      q = query(collection(this.c.store, 'rooms'), where('workspaceId', '==', wsId), where('visibility', '==', 'listed'));
    }
    const roomDocs = await getDocs(q);
    const rooms = roomDocs.docs.map(d => {
      const data = { ...d.data(), id: d.id } as Room;
      return new RoomSimplified(data);
    }).filter(r => !r.isOneToOne && r.visibility !== 'personal');

    return { rooms, status: 'SUCCESS' };
  }

  async getJoinedWorkspaceRooms(data: GetWorkspaceRoomsRequest): Promise<GetWorkspaceRoomsResponse> {
    const f = getFunc('getJoinedWorkspaceRooms');
    const res = await f(pojo(data));
    return res.data as GetWorkspaceRoomsResponse;
  }


  async getWorkspaceByInvitationKey(data: GetWSByInvitationKeyRequest): Promise<GetWSByInvitationKeyResponse> {
    const f = getFunc('getWorkspaceByInvitationKey');
    const res = await f(pojo(data));
    return res.data as GetWSByInvitationKeyResponse;
  }

  async joinRoomViaWorkspace(data: JoinRoomViaWorkspaceRequest): Promise<JoinRoomViaWorkspaceResponse> {
    const f = getFunc('joinRoomViaWorkspace');
    const res = await f(pojo(data));
    return res.data as JoinRoomViaWorkspaceResponse;
  }

  async getAllowedWorkspaces(domain: string) {
    const q = query(collection(this.c.store, Workspace.COL), where('emailDomains', 'array-contains', domain));
    const snap = await getDocs(q);
    return snap.docs.map(d => d.data() as Workspace);
  }

  /**
   * Sync owner info if current user is owner and info has changed
   */
  private async syncOwnerInfoIfNeeded(workspace: Workspace) {
    if (!My.user || !workspace.owner || workspace.owner.id !== My.user.id) return;

    const currentOwner = workspace.owner;
    const myInfo = toUserSimplified(My.user);

    // Check if name or photo has changed
    if (currentOwner.name !== myInfo.name || currentOwner.photoUrl !== myInfo.photoUrl) {
      console.log(`[Workspace] Syncing owner info for ${workspace.id}`);
      try {
        await updateDoc(doc(this.c.store, Workspace.COL, workspace.id), {
          owner: pojo(myInfo)
        });
      } catch (e) {
        console.error('[Workspace] Failed to sync owner info:', e);
      }
    }
  }

  subscribeToWorkspaces(userId: string, handler: (list: Workspace[]) => void) {
    const q = query(collectionGroup(this.c.store, WorkspaceMember.COL), where('id', '==', userId));

    // 워크스페이스 문서별 실시간 구독 해제 함수 목록
    let wsUnsubscribes: (() => void)[] = [];
    // 현재 워크스페이스 데이터 캐시 (ID → Workspace)
    const wsCache = new Map<string, Workspace>();

    const memberUnsub = onSnapshot(q, async (snap) => {
      // 이전 워크스페이스 구독 해제
      wsUnsubscribes.forEach(unsub => unsub());
      wsUnsubscribes = [];
      wsCache.clear();

      const workspaceIds: string[] = [];
      for (const docSnap of snap.docs) {
        const pathParts = docSnap.ref.path.split('/');
        const workspaceId = pathParts[1];
        workspaceIds.push(workspaceId);
      }

      if (workspaceIds.length === 0) {
        handler([]);
        return;
      }

      // 각 워크스페이스 문서에 실시간 구독
      for (const wsId of workspaceIds) {
        const wsRef = doc(this.c.store, Workspace.COL, wsId);
        const unsub = onSnapshot(wsRef, (wsDoc) => {
          if (wsDoc.exists()) {
            const ws = { id: wsDoc.id, ...wsDoc.data() } as Workspace;
            wsCache.set(wsId, ws);
            this.syncOwnerInfoIfNeeded(ws);
          } else {
            wsCache.delete(wsId);
          }
          // 모든 워크스페이스 데이터가 모이면 handler 호출
          if (wsCache.size >= 0) {
            handler(Array.from(wsCache.values()));
          }
        });
        wsUnsubscribes.push(unsub);
      }
    });

    // cleanup: 멤버 구독 + 모든 워크스페이스 구독 해제
    return () => {
      memberUnsub();
      wsUnsubscribes.forEach(unsub => unsub());
    };
  }

  async getWorkspace(workspaceId: string): Promise<Workspace | undefined> {
    const d = doc(this.c.store, Workspace.COL, workspaceId)
    return (await getDoc(d)).data() as Workspace | undefined;
  }

  leaveWorkspace(workspaceId: string, userId: string) {
    return deleteDoc(doc(this.c.store, Workspace.COL, workspaceId, WorkspaceMember.COL, userId));
  }

  async updateWorkspace(workspace: Workspace) {
    console.log('[FireClientWorkspace] updateWorkspace called', {
      id: workspace.id,
      name: workspace.name,
      hasOwner: !!workspace.owner
    });

    try {
      const pojoData = pojo(workspace);
      console.log('[FireClientWorkspace] pojo conversion successful, keys:', Object.keys(pojoData));

      const docRef = doc(this.c.store, Workspace.COL, workspace.id);
      console.log('[FireClientWorkspace] docRef created, calling updateDoc...');

      const result = await updateDoc(docRef, pojoData);
      console.log('[FireClientWorkspace] updateDoc completed successfully');
      return result;
    } catch (e) {
      console.error('[FireClientWorkspace] updateDoc failed:', e);
      throw e;
    }
  }


  deleteWorkspace(workspaceId: string) {
    return deleteDoc(doc(this.c.store, Workspace.COL, workspaceId));
  }

  async getWorkspaceMembers(workspaceId: string): Promise<WorkspaceMember[]> {
    const q = query(collection(this.c.store, Workspace.COL, workspaceId, WorkspaceMember.COL));
    const snap = await getDocs(q);
    const members = snap.docs.map(d => {
      const data = d.data() as WorkspaceMember;
      // members 서브컬렉션에 저장된 lastActiveAt 변환
      if ((data as any).lastActiveAt) {
        const ts = (data as any).lastActiveAt;
        (data as any).lastActiveAt = ts?.toDate ? ts.toDate() : new Date(ts);
      }
      return data;
    });
    return members;
  }

  /**
   * 워크스페이스 멤버의 lastActiveAt을 하루에 한 번 업데이트
   * 룸 진입 시 호출됨
   */
  async updateMemberLastActive(workspaceId: string) {
    const userId = My.user?.id;
    if (!userId || !workspaceId) return;

    const storageKey = `lastActive:${workspaceId}`;
    const todayStr = new Date().toISOString().split('T')[0];
    const lastStr = localStorage.getItem(storageKey);

    if (lastStr === todayStr) return; // 오늘 이미 업데이트함

    try {
      const memberRef = doc(this.c.store, Workspace.COL, workspaceId, WorkspaceMember.COL, userId);
      // 멤버 문서가 존재하는지 먼저 확인 (워크스페이스 생성 직후 아직 없을 수 있음)
      const memberSnap = await getDoc(memberRef);
      if (!memberSnap.exists()) return;
      await updateDoc(memberRef, { lastActiveAt: new Date() });
      localStorage.setItem(storageKey, todayStr);
    } catch (e) {
      console.warn('[Workspace] updateMemberLastActive failed (non-fatal):', e);
    }
  }

  async getWorkspaceStatus(workspaceId: string): Promise<WorkspaceStatus> {
    const d = doc(this.c.store, Workspace.COL, workspaceId, WorkspaceStatus.COL, WorkspaceStatus.DOC);
    const status = (await getDoc(d)).data() as WorkspaceStatus | undefined;
    return status ?? new WorkspaceStatus(workspaceId);
  }

  subscribeToUnifiedReport(workspaceId: string, handler: (report: UnifiedStorageReport | undefined) => void) {
    const d = doc(this.c.store, Workspace.COL, workspaceId, 'reports', 'storage_daily');
    return onSnapshot(d, (snap) => {
      handler(snap.exists() ? snap.data() as UnifiedStorageReport : undefined);
    });
  }

  async getUnifiedReport(workspaceId: string): Promise<UnifiedStorageReport | undefined> {
    const d = doc(this.c.store, Workspace.COL, workspaceId, 'reports', 'storage_daily');
    const snap = await getDoc(d);
    return snap.exists() ? snap.data() as UnifiedStorageReport : undefined;
  }

  /**
   * Super Admin용: 모든 워크스페이스 조회
   */
  async getAllWorkspaces(): Promise<Workspace[]> {
    const q = query(collection(this.c.store, Workspace.COL));
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }) as Workspace);
  }

  async removeWorkspaceMember(data: RemoveWorkspaceMemberRequest): Promise<RemoveWorkspaceMemberResponse> {
    const f = getFunc('removeWorkspaceMember');
    const res = await f(pojo(data));
    return res.data as RemoveWorkspaceMemberResponse;
  }

  async changeMemberRole(data: ChangeMemberRoleRequest): Promise<ChangeMemberRoleResponse> {
    const f = getFunc('changeMemberRole');
    const res = await f(pojo(data));
    return res.data as ChangeMemberRoleResponse;
  }

  /** 현재 유저의 WorkspaceMember 문서 조회 (permissions 포함) */
  async getMyMember(workspaceId: string): Promise<WorkspaceMember | null> {
    const userId = My.user?.id;
    if (!userId) return null;
    const d = doc(this.c.store, Workspace.COL, workspaceId, WorkspaceMember.COL, userId);
    const snap = await getDoc(d);
    return snap.exists() ? snap.data() as WorkspaceMember : null;
  }

  /**
   * 워크스페이스 진입 시 본인 멤버십을 Supabase workspace_members 미러에 self-heal (idempotent upsert).
   * boards/events 등 Supabase RLS 가 미러를 참조하므로, 미러 누락 시 정상 멤버가 차단되는 것을 방지.
   * 서버측 5분 캐시 + 본인 것만 처리. 실패는 비치명적이라 무시(다음 진입 시 재시도).
   */
  async ensureMemberSync(workspaceId: string): Promise<void> {
    try {
      const f = getFunc('ensureWorkspaceMemberSync');
      await f({ workspaceId });
    } catch (e) {
      console.warn('[ensureMemberSync] self-heal 실패(무시):', e);
    }
  }

  async transferRoomCreator(data: TransferRoomCreatorRequest): Promise<TransferRoomCreatorResponse> {
    const f = getFunc('transferRoomCreator');
    const res = await f(pojo(data));
    return res.data as TransferRoomCreatorResponse;
  }

  async deleteRoomAsAdmin(data: DeleteRoomAsAdminRequest): Promise<DeleteRoomAsAdminResponse> {
    const f = getFunc('deleteRoomAsAdmin');
    const res = await f(pojo(data));
    return res.data as DeleteRoomAsAdminResponse;
  }

  async joinRoomAsCreator(data: JoinRoomAsCreatorRequest): Promise<JoinRoomAsCreatorResponse> {
    const f = getFunc('joinRoomAsCreator');
    const res = await f(pojo(data));
    return res.data as JoinRoomAsCreatorResponse;
  }
}

// async getListedWorkspaceRooms(workspaceId: string) {
//   console.log('getAllowedWorkspaces', workspaceId);
//   const q = query(collection(this.c.store, Room.COL),
//   where('workspaceId', 'array-contains', workspaceId),
//   where('visibility', '==', 'listed')
//   );
//   const snap = await getDocs(q);
//   return snap.docs.map(d => d.data() as Workspace);
// }